import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import FAQ from '../components/FAQ';
import ContactCTA from '../components/ContactCTA';
import Testimonials from '../components/Testimonials';
import { combineSchemas, organizationSchema, breadcrumbSchema, faqSchema } from '../lib/schema';
import { SITE_URL } from '../components/SEO';
import { CheckCircle2, ArrowRight } from 'lucide-react';

const toc = [
  { id: 'what-is', label: 'What is website design?' },
  { id: 'types', label: 'Types of website design' },
  { id: 'process', label: 'The website design process' },
  { id: 'cost', label: 'How much does website design cost?' },
  { id: 'timeline', label: 'How long does website design take?' },
  { id: 'diy-vs-agency', label: 'DIY vs freelancer vs agency' },
  { id: 'niches', label: 'Website design by industry' },
  { id: 'seo', label: 'Website design and SEO' },
  { id: 'ai', label: 'Website design and A.I.' },
  { id: 'faqs', label: 'FAQs' },
];

const faqs = [
  { q: 'What is the difference between website design and website development?', a: 'Website design is the visual, structural and user-experience side — what the site looks like and how visitors interact with it. Website development is the technical build — the code that makes it work. A good agency handles both.' },
  { q: 'How long does professional website design take?', a: 'A small business site usually launches in 2–4 weeks. A medium custom site with integrations takes 4–8 weeks. Enterprise or eCommerce builds can take 8–16 weeks depending on complexity.' },
  { q: 'How much should I budget for professional website design?', a: 'Expect $2,000–$6,000 for a small business informational site, $6,000–$15,000 for a custom site with integrations, and $15,000+ for eCommerce or enterprise builds.' },
  { q: 'Do I need to hire a designer or can I use a template?', a: 'Templates work for hobby projects. Real businesses that depend on the site for leads or sales should invest in custom design. The revenue you gain from a professionally designed site pays for it many times over.' },
  { q: 'What should a professional website include in 2025?', a: 'Mobile-first responsive design, page-speed under 3 seconds, clear CTAs above the fold, HTTPS security, structured data for SEO, accessibility compliance (WCAG 2.1 AA), and analytics tracking at a minimum.' },
  { q: 'Will my website rank on Google after launch?', a: 'A newly launched site typically takes 3–6 months to start ranking for competitive terms. Local and long-tail keywords can rank in 4–12 weeks with proper on-page SEO, local citations and content publishing.' },
];

const UltimateGuidePage = () => {
  const path = '/website-design-guide';
  const schema = combineSchemas(
    organizationSchema,
    breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Website Design Guide', path }]),
    faqSchema(faqs),
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'The Complete Guide to Website Design in 2025',
      description: 'The definitive guide to website design: process, cost, timelines, industry considerations, SEO and A.I. in 2025.',
      author: { '@type': 'Organization', name: 'Website Symphony' },
      publisher: { '@type': 'Organization', name: 'Website Symphony', logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` } },
      datePublished: '2025-01-15',
      dateModified: new Date().toISOString().split('T')[0],
      mainEntityOfPage: `${SITE_URL}${path}`,
    }
  );

  return (
    <Layout>
      <SEO
        title="Website Design: The Complete 2025 Guide | Website Symphony"
        description="The definitive guide to website design in 2025: what it costs, how long it takes, DIY vs agency, industry-specific considerations, SEO, A.I. and more. Written by Website Symphony."
        keywords="website design, web design, custom website design, website design guide, website design cost, website design process, professional website design"
        canonical={path}
        schema={schema}
      />
      <Breadcrumbs items={[{ name: 'Website Design Guide', path }]} />

      <article className="bg-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <span className="text-[#D9A04A] tracking-[0.3em] text-sm font-semibold">THE COMPLETE GUIDE</span>
          <h1 className="text-[#2C5F66] text-4xl md:text-6xl font-extrabold leading-tight mt-3">Website Design in 2025: Everything You Need to Know</h1>
          <p className="text-gray-600 text-lg leading-relaxed mt-6">A definitive, no-fluff guide to modern website design — written by the team at Website Symphony after 20 years and 750+ launched websites.</p>
          <div className="flex items-center gap-4 mt-6 text-sm text-gray-500">
            <span>Updated {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
            <span>·</span>
            <span>18 min read</span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 pb-16 grid md:grid-cols-[220px_1fr] gap-10">
          {/* TOC */}
          <aside className="md:sticky md:top-24 self-start">
            <p className="text-[#2C5F66] font-bold mb-3 text-sm tracking-wider">CONTENTS</p>
            <nav className="space-y-2 text-sm">
              {toc.map((t) => (
                <a key={t.id} href={`#${t.id}`} className="block text-gray-600 hover:text-[#D9A04A] transition-colors">{t.label}</a>
              ))}
            </nav>
          </aside>

          <div className="blog-content">
            <section id="what-is">
              <h2>What is website design?</h2>
              <p>Website design is the practice of planning, structuring and visually crafting the pages of a website so that visitors can find what they need, trust the business behind it, and take action. It combines <strong>user experience (UX)</strong>, <strong>user interface (UI)</strong>, <strong>brand identity</strong> and <strong>conversion strategy</strong> into a single working product.</p>
              <p>A well-designed website is not just pretty. It is fast, accessible on every device, findable on Google, secure by default, and engineered to turn strangers into customers.</p>
            </section>

            <section id="types">
              <h2>Types of website design</h2>
              <h3>Informational websites</h3>
              <p>The most common category. These sites exist to inform visitors and generate leads: service pages, portfolio, about, blog, contact. Most small business sites fall here.</p>
              <h3>eCommerce websites</h3>
              <p>Sites built to sell products online. Requires payment gateways, inventory management, shipping calculations and tax handling. Common platforms: Shopify, WooCommerce, custom.</p>
              <h3>Booking-driven websites</h3>
              <p>Sites where the primary action is booking a service — med spas, salons, tattoo studios, medical practices. Requires calendar integration and often deposits.</p>
              <h3>B2B and enterprise websites</h3>
              <p>Sites designed to educate and pre-qualify decision makers. Heavy on downloadable content, case studies, RFP forms and multi-language support.</p>
              <h3>Portfolio and personal brand websites</h3>
              <p>Sites for creatives, coaches, speakers and freelancers. Design-forward, content-driven, with clear calls to action to book calls or purchase products.</p>
            </section>

            <section id="process">
              <h2>The website design process</h2>
              <p>Every serious website goes through five phases:</p>
              <ol>
                <li><strong>Discovery and strategy.</strong> Understand who the customer is, what they need, and what action the site should drive.</li>
                <li><strong>Wireframes and information architecture.</strong> Map out every page and how they connect — before any pixel is designed.</li>
                <li><strong>Visual design.</strong> Apply brand identity, typography, color, imagery and micro-interactions.</li>
                <li><strong>Development.</strong> Build the site on the right platform — WordPress, Shopify, custom React or Next.js — with SEO, speed and accessibility baked in.</li>
                <li><strong>Testing, launch and iteration.</strong> QA on every device, deploy, monitor performance, and iterate based on real user data.</li>
              </ol>
            </section>

            <section id="cost">
              <h2>How much does website design cost?</h2>
              <p>Website design pricing varies wildly. Here is what to expect in 2025:</p>
              <ul>
                <li><strong>DIY builders (Wix, Squarespace):</strong> $180–$600 per year + your time.</li>
                <li><strong>Freelance designer:</strong> $1,500–$7,500 depending on experience.</li>
                <li><strong>Small agency:</strong> $6,000–$15,000 for a custom small business site.</li>
                <li><strong>Mid-size to enterprise agency:</strong> $15,000–$100,000+ for complex builds.</li>
              </ul>
              <p>See our <Link to="/blog/true-cost-of-custom-website-2025">honest pricing breakdown</Link> for a deeper dive.</p>
            </section>

            <section id="timeline">
              <h2>How long does website design take?</h2>
              <p>Timelines depend on scope, decision speed and content readiness:</p>
              <ul>
                <li><strong>Small business site (5–10 pages):</strong> 2–4 weeks</li>
                <li><strong>Custom site with integrations:</strong> 4–8 weeks</li>
                <li><strong>eCommerce:</strong> 6–12 weeks</li>
                <li><strong>Enterprise or multi-language:</strong> 12–24 weeks</li>
              </ul>
              <p>The single biggest delay in every project is client-side content approval. Have your copy, photos and brand assets ready before kickoff.</p>
            </section>

            <section id="diy-vs-agency">
              <h2>DIY vs freelancer vs agency</h2>
              <p>Choose DIY when: your budget is under $500 and the site is a hobby or MVP.</p>
              <p>Choose a freelancer when: you have $2,500–$7,500, you can project-manage, and you can write your own copy.</p>
              <p>Choose an agency when: your website is critical to revenue and you want strategy, copywriting, design, development, launch support and post-launch iteration handled for you.</p>
              <p>Compare in detail: <Link to="/compare/custom-web-design-vs-wix">Custom Web Design vs Wix</Link>, <Link to="/compare/custom-web-design-vs-squarespace">Custom vs Squarespace</Link>, <Link to="/compare/custom-web-design-vs-wordpress">Custom vs WordPress</Link>.</p>
            </section>

            <section id="niches">
              <h2>Website design by industry</h2>
              <p>Every industry has unique requirements. Some of the niches Website Symphony specializes in:</p>
              <ul>
                <li><Link to="/web-design-for-med-spas">Web design for med spas</Link> — online booking, HIPAA-aware forms, before-and-after galleries.</li>
                <li><Link to="/web-design-for-tattoo-shops">Web design for tattoo shops</Link> — artist portfolios, Instagram sync, deposit collection.</li>
                <li><Link to="/web-design-for-construction-companies">Web design for construction companies</Link> — project galleries, bid request forms, credential displays.</li>
                <li><Link to="/web-design-for-oil-rig-companies">Web design for oil rig and energy companies</Link> — capability statements, HSE stats, multi-language.</li>
              </ul>
            </section>

            <section id="seo">
              <h2>Website design and SEO</h2>
              <p>A beautiful website that no one finds is worthless. Modern website design must include SEO from day one, not as an afterthought.</p>
              <p>Non-negotiable SEO fundamentals: fast page load, mobile-first responsive layout, semantic HTML, schema.org structured data, XML sitemap, robots.txt, secure HTTPS, alt text on every image, and unique meta titles and descriptions per page.</p>
              <p>See our <Link to="/blog/local-seo-service-business-playbook">Local SEO Playbook</Link> for the deep dive on ranking locally.</p>
            </section>

            <section id="ai">
              <h2>Website design and A.I.</h2>
              <p>By 2025 every serious website should have at least one AI-powered feature: a chatbot that qualifies leads while you sleep, dynamic personalization that shows the right offer to each visitor, or content automation that keeps the blog fresh.</p>
              <p>AI is also changing how people find websites. See our guide on <Link to="/blog/how-to-rank-in-ai-search-chatgpt-perplexity-gemini">how to rank in AI search</Link> — ChatGPT, Perplexity and Gemini are rapidly becoming primary search interfaces.</p>
            </section>

            <section id="faqs">
              <h2>Frequently asked questions</h2>
              <p>Below are the most common questions we hear from businesses about website design. If yours is not here, <Link to="/contact">get in touch</Link> and we will answer it.</p>
            </section>
          </div>
        </div>
      </article>

      <FAQ items={faqs} title="Website Design FAQs" />
      <Testimonials />
      <ContactCTA defaultService="Website Design Guide" heading="Ready to start your website?" subheading="Get a free consultation and fixed-price quote in 24 hours." />
    </Layout>
  );
};

export default UltimateGuidePage;
